/**
 * CR-7782: the Call Off Management list's Columns control — show or hide each
 * column and put them in the order the viewer wants.
 *
 * A popover rather than a dropdown menu: a menu takes the pointer and the
 * keyboard for its own item navigation, which fights a drag. Each row can be
 * dragged by its handle, or moved with its up / down buttons for anyone not
 * using a mouse. Reference is fixed: it is how a load is identified, so it
 * cannot be hidden, but it can still be moved.
 *
 * @link useCallOffColumns for the layout this edits.
 */
import { useState } from 'react';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import {
  ArrowDown, ArrowUp, Columns as ColumnsIcon, GripVertical,
} from 'lucide-react';
import type { CallOffColumn } from './callOffColumnConfig';

interface CallOffColumnsMenuProps {
  /** Every column offered to this viewer, in the viewer's order. */
  columns: readonly CallOffColumn[];
  /** Whether a column is showing (its checkbox is ticked). */
  isVisible: (id: string) => boolean;
  /** Show or hide a column. Ignored for a fixed column. */
  onToggle: (id: string) => void;
  /** Move the column at one index to another. */
  onMove: (from: number, to: number) => void;
  /** Show every column, keeping the order. */
  onShowAll: () => void;
  /** Back to the role's default columns and order. */
  onReset: () => void;
  /** How many offered columns are hidden, for the button's "(n hidden)". */
  hiddenCount: number;
}

export default function CallOffColumnsMenu({
  columns, isVisible, onToggle, onMove, onShowAll, onReset, hiddenCount,
}: CallOffColumnsMenuProps) {
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const endDrag = () => {
    setDragFrom(null);
    setDragOver(null);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <ColumnsIcon className="h-4 w-4" />
          Columns
          {hiddenCount > 0 && (
            <span className="text-xs text-muted-foreground">({hiddenCount} hidden)</span>
          )}
        </Button>
      </PopoverTrigger>
      {/* Capped to the room Radix finds on screen, so the list scrolls
          rather than the popover running off the bottom of the page. */}
      <PopoverContent
        align="end"
        className="flex w-72 max-h-[var(--radix-popover-content-available-height)] flex-col p-0"
      >
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">Manage Columns</span>
          <div className="flex items-center gap-2 text-xs">
            <button type="button" className="text-primary hover:underline" onClick={onShowAll}>
              Show all
            </button>
            <span className="text-muted-foreground">|</span>
            <button type="button" className="text-primary hover:underline" onClick={onReset}>
              Reset
            </button>
          </div>
        </div>
        <p className="border-b bg-muted/30 px-3 py-1 text-xs text-muted-foreground">
          Drag to reorder &middot; Tick to show
        </p>
        <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-2" aria-label="Columns">
          {columns.map((column, index) => (
            <li
              key={column.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', String(index));
                setDragFrom(index);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(index);
              }}
              onDragLeave={() => setDragOver((current) => (current === index ? null : current))}
              onDrop={(e) => {
                e.preventDefault();
                const from = dragFrom ?? parseInt(e.dataTransfer.getData('text/plain'), 10);
                if (!Number.isNaN(from) && from !== index) onMove(from, index);
                endDrag();
              }}
              onDragEnd={endDrag}
              className={`group flex items-center gap-2 rounded px-1.5 py-1 hover:bg-muted/50 ${
                dragFrom === index ? 'opacity-50' : ''
              } ${dragOver === index && dragFrom !== index ? 'bg-primary/10' : ''}`}
            >
              <GripVertical
                className="h-4 w-4 shrink-0 cursor-move text-muted-foreground/50 group-hover:text-muted-foreground"
                aria-hidden="true"
              />
              <Checkbox
                id={`call-off-column-${column.id}`}
                checked={isVisible(column.id)}
                disabled={column.fixed}
                onCheckedChange={() => onToggle(column.id)}
              />
              <label
                htmlFor={`call-off-column-${column.id}`}
                className={`flex-1 text-sm ${column.fixed ? 'text-muted-foreground' : 'cursor-pointer'}`}
              >
                {column.label}
              </label>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                disabled={index === 0}
                aria-label={`Move ${column.label} up`}
                onClick={() => onMove(index, index - 1)}
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                disabled={index === columns.length - 1}
                aria-label={`Move ${column.label} down`}
                onClick={() => onMove(index, index + 1)}
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
        <p className="border-t bg-muted/30 px-3 py-1.5 text-xs text-muted-foreground">
          Reference is always shown
        </p>
      </PopoverContent>
    </Popover>
  );
}
